import { Button, Col, Form, Input, Radio, Row, Select, Spin, Tag } from "antd";
import { useEffect, useRef } from "react";
import Icon from "../../../../assets/icons/icon";
import DateSelect from "../../../../components/form/dateSelect";
import EnumSelect from "../../../../components/form/enumSelect";
import InputNum from "../../../../components/form/inputNum";
import DividerLine from "../../../../components/lineSplit";
import _t from "../../../../lang/translate";
import { Filters, PaginatedAdverts } from "./types";
import useAdvertOptions, { clearDependentFields } from "./useAdvertOptions";


type FiltersFormProps = {
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  data: PaginatedAdverts;
  setData: React.Dispatch<React.SetStateAction<PaginatedAdverts>>
  loadingData: boolean;
}



export default function FiltersForm({ filters, setFilters, data, loadingData }: FiltersFormProps) {
  const [form] = Form.useForm<Filters>()
  const { handleDrillDown, loadingOptionsFields, options } = useAdvertOptions()
  const didRenderRef = useRef(false)
  const initialState = useRef(filters)


  useEffect(() => {
    if (didRenderRef.current) return;
    initializeFetches()
    didRenderRef.current = true;
  }, [])

  async function initializeFetches() {
    if (filters.type) {
      await handleDrillDown(filters, "type")
    }
    if (filters.manufacturer) {
      await handleDrillDown(filters, "manufacturer")
    }
    if (filters.model) {
      await handleDrillDown(filters, "model")
    }
  }


  function handleReset() {
    setFilters(form.getFieldsValue())
  }

  function handleValuesChange(changedValue: Partial<Filters>, values: Filters) {
    let key = Object.keys(changedValue)[0] as keyof Filters
    key = key === 'searchDate' ? "type" : key
    let cleaned = values
    if (key) {
      cleaned = clearDependentFields({ ...values }, key)
    }
    form.setFieldsValue(cleaned);
    setFilters(cleaned)
    handleDrillDown(cleaned, key)
  }
  function clearField(key: keyof Filters) {
    const values = form.getFieldsValue()
    values[key] = undefined
    form.setFieldsValue(values)
    setFilters(values)
  }
  return (
    <Form className="market-place-filters" layout="horizontal"
      form={form}
      onValuesChange={handleValuesChange}
      initialValues={initialState.current}
      onReset={handleReset}
      labelCol={{ xs: 24, lg: 8, flex: "0 0 64px" }}
      colon={false}
      labelAlign="left"
    >
      <Row justify="space-between">
        <Col>
          <Spin size="large" style={{ top: 10, left: 12 }} spinning={loadingData}> </Spin>
        </Col>
        <Col>
          <Tag className="ant-tag-exception">{data?.data?.length} stk.</Tag>
        </Col>
      </Row>
      <DividerLine className="mt-05 mb-05" />
      <Form.Item label={_t("from", "date_at")} labelCol={{ flex: "0 0 64px" }}
        labelAlign="left" name="searchDate"
        style={{ flexDirection: "row", alignItems: "center" }}
      >
        <DateSelect />
      </Form.Item>

      <Row justify="space-between" align="middle">
        <Col> <h4 className="mb-0">Filtre</h4> </Col>
        <Col>
          <Button htmlType="reset" size="small" type="link">{_t("reset")}</Button>
        </Col>
      </Row>
      <DividerLine className="mt-1 mb-1" />
      <Form.Item label={_t("status")} name="status">
        <Radio.Group optionType="button" buttonStyle="solid"
          options={[
            { label: "Til salg", value: 0 },
            { label: "Solgte", value: 1 },
            { label: "Alle", value: 2 },
          ]}
        />
      </Form.Item>

      <Form.Item name={['type']} label={_t("type")}>
        <Select loading={loadingOptionsFields.has("type")}
          showSearch
          placeholder={_t("choose", "type")}
          optionFilterProp="label"
          dropdownMatchSelectWidth={400}
          allowClear
          options={options.type} />
      </Form.Item>


      <Form.Item name={['manufacturer']} label={_t("brand")}>
        <Select loading={loadingOptionsFields.has("manufacturer")}
          disabled={!options.manufacturer?.length}
          showSearch
          placeholder={_t("choose", "brand")}
          optionFilterProp="label"
          dropdownMatchSelectWidth={400}
          allowClear
          options={options.manufacturer} />
      </Form.Item>

      <Form.Item name={['model']} label={_t("model")} >
        <Select loading={loadingOptionsFields.has("model")}
          disabled={!options.model?.length}
          showSearch
          placeholder={_t("choose", "model")}
          optionFilterProp="label"
          dropdownMatchSelectWidth={400}
          allowClear
          options={options.model} />
      </Form.Item>
      <Form.Item name={['variant']} label={_t("variant")}>
        <Select
          loading={loadingOptionsFields.has("variant")}
          disabled={!options.variant?.length}
          showSearch
          placeholder={_t("choose", "variant")}
          optionFilterProp="label"
          dropdownMatchSelectWidth={400}
          allowClear
          options={options.variant} />
      </Form.Item>

      <Form.Item name="search">
        <Input size="large" placeholder={_t("search_for_model")}
          suffix={<ClearButton onClick={() => clearField('search')} />}
        />
      </Form.Item>

      <DoubleItem
        label={_t("regYear")}
        name1="regYearFrom"
        name2="regYearTo"
        input1={<InputNum disableParsingAndFormatting placeholder={_t("regYear", "from")}
          suffix={<ClearButton onClick={() => clearField('regYearFrom')} />}
        />}
        input2={<InputNum disableParsingAndFormatting placeholder={_t("regYear", "to")}
          suffix={<ClearButton onClick={() => clearField('regYearTo')} />}
        />}
      />

      <DoubleItem
        label={_t("mileage_unit")}
        name1="mileageFrom"
        name2="mileageTo"
        input1={<InputNum placeholder={_t("mileage", "from")} suffix={<ClearButton onClick={() => clearField('mileageFrom')} />} />}
        input2={<InputNum placeholder={_t("mileage", "to")} suffix={<ClearButton onClick={() => clearField('mileageTo')} />} />}
      />
      <DoubleItem
        label={_t("price")}
        name1="priceFrom"
        name2="priceTo"
        input1={<InputNum placeholder={_t("price", "from")} suffix={<ClearButton onClick={() => clearField('priceFrom')} />} />}
        input2={<InputNum placeholder={_t("price", "to")} suffix={<ClearButton onClick={() => clearField('priceTo')} />} />}
      />

      <DoubleItem
        label={_t("horse_power")}
        name1="horsePowerFrom"
        name2="horsePowerTo"
        input1={<InputNum placeholder={_t("horse_power", "from")} suffix={<ClearButton onClick={() => clearField('horsePowerFrom')} />} />}
        input2={<InputNum placeholder={_t("horse_power", "to")} suffix={<ClearButton onClick={() => clearField('horsePowerTo')} />} />}
      />

      {/* <DoubleItem */}
      {/*   label={_t("engine_size_short")} */}
      {/*   name1="engineSizeFrom" */}
      {/*   name2="engineSizeTo" */}
      {/*   input1={<InputNum placeholder={_t("engine_size", "from")} suffix={<ClearButton onClick={() => clearField('engineSizeFrom')} />} />} */}
      {/*   input2={<InputNum placeholder={_t("engine_size", "to")} suffix={<ClearButton onClick={() => clearField('engineSizeTo')} />} />} */}
      {/* /> */}
      <Form.Item label={_t("trim")} name="body">
        <EnumSelect placeholder={_t("choose", "trim")} allowClear nameKey={["vehicle", "body"]} />
      </Form.Item>



      <Row wrap={false} gutter={[8, 8]}>
        <Col span={12}>
          <Form.Item name={"fuel"} label={<Icon size="large" fill={"none"} name="drivmiddel" />}
            labelCol={{ flex: "0 0 24px" }}
            wrapperCol={{ style: { flexWrap: "nowrap" } }}
          >
            <EnumSelect nameKey={['vehicle', 'fuel']} allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={"gearbox"} label={<Icon size="large" fill={"none"} name="gearkasse" />}
            labelCol={{ flex: "0 0 24px" }}
            wrapperCol={{ style: { flexWrap: "nowrap" } }}
          >
            <EnumSelect nameKey={['vehicle', 'gearbox']} allowClear />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label={_t("price", "type")} name={["priceType"]}>
        <EnumSelect allowClear nameKey={['adCatalog', 'priceType']}
          allLabel="Alle" includeAllOption
        />
      </Form.Item>

    </Form>

  )
}

function ClearButton({ onClick }: { onClick: () => void }) {
  return (
    <Button tabIndex={-1} className="clear-btn"
      style={{ marginRight: -2 }}
      onClick={onClick}
      type="text" shape="circle"
      icon={<Icon size="small" name="close-outline" />}
    />
  )
}
function DoubleItem({ label, name1, name2, input1, input2, }: {
  label?: string
  name1: string
  name2: string
  input1: React.ReactNode
  input2: React.ReactNode
}) {
  return (
    <Form.Item label={label} className="double-form-item">
      <Row wrap={false} gutter={[8, 8]}>
        <Col span={12}>
          <Form.Item name={name1} noStyle>
            {input1}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={name2} noStyle>
            {input2}
          </Form.Item>
        </Col>
      </Row>
    </Form.Item>
  );
}

























